import React from 'react'
import {Lang} from 'yougile-lang'
import {Hub, HubUtil} from 'yougile-model'
import {Items} from '@api/model/items'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {showMoreDeleteTaskKey} from '@client/storage'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {Column} from '@extensions/column'
import {GanttDraft} from '@extensions/gantt-draft'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {canShowTask} from '@extensions/mirrored-tasks/permissions'
import {Task} from '@extensions/task'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {TaskItem} from '../my-tasks/task-item'
import {TaskMenuNewBase} from './task-menu-new-base'

export const dealMenuVisibleItems: TaskMenuVisibleItems[] = [
  'rename-task',
  'link',
  'select-multiple',
  'delete'
] as const

export type TaskMenuVisibleItems =
  | 'add-subtask'
  | 'create-task-mirror'
  | 'complete-task'
  | 'rename-task'
  | 'make-public'
  | 'move-task'
  | 'archive'
  | 'duplicate'
  | 'link'
  | 'select-multiple'
  | 'upload-cover'
  | 'remove-from-board'
  | 'task-colorpicker'
  | 'delete'
  | 'remove-from-subtasks'

export interface Props {
  task: TaskItem
  rename?: () => void
  removeFromSubtasks?: () => void
  addSubtask?: () => void
  duplicate?: boolean
  removeFromBoard?: boolean
  className?: string
  fromWhere: 'my-tasks' | 'board' | 'right-panel' | 'timeline'
  completed?: boolean
  isParentTaskTemplate?: boolean
  canSelectMultiple?: boolean
  asSubtask?: boolean
  onToggle?: (value: boolean) => void
  visibleItems?: TaskMenuVisibleItems[]
  withBorder?: boolean
}

export const TaskMenuNew = (props: Props) => {
  const {task, fromWhere} = props
  const box = Sync.getCurrentSandbox()
  const hub = box.get(task.id) as Hub

  if (SharedBoardsClient.isGuest()) {
    return null
  }

  const isAnyTaskTemplate =
    props.isParentTaskTemplate || Task.isTaskTemplate(hub)
  const canShow = isAnyTaskTemplate || canShowTask(hub)

  if (!canShow) {
    return null
  }

  const toggleComplete = () =>
    Task.changeCompleted(
      {...task, companyId: box.companyId},
      !task.completedAt,
      fromWhere
    )

  const toggleArchive = () =>
    Task.changeArchived({...task, companyId: box.companyId}, !task.archivedAt)

  const deleteTask = async () => {
    const mirror = isMirroredHub(hub)
    const text = mirror
      ? Lang.msg.wantToDeleteAMirror
      : Lang.msg.wantToDeleteATask

    if (await showDeleteConfirm(text, showMoreDeleteTaskKey)) {
      if (fromWhere === 'timeline') {
        void Tracking.send(TrackingEvent.ganttDeleteTaskViaGanttChart)
      }

      if (!Items.beforeDelete(Items.fromHub(hub))) {
        return
      }

      box.remove(hub)
      Items.delete(Items.fromHub(hub))
      GanttDraft.planEntitySubtree(hub.id, box)
      if (HubUtil.isPhantom(hub)) {
        taskPhantomObserver.update()
      }
    }
  }

  const moveTask = (chosen: Hub[]) => {
    if (chosen.length > 0) {
      const column = chosen[0]
      Column.taskDropHandler(0, hub, column, {
        ignoreBoardFilters: true,
        type: 'menu'
      })
    }
  }

  const selectMultipleTasksToggleOn = () => {
    if (!multiActionsWithTasksStore.state.visible) {
      multiActionsWithTasksStore.visibleOn()
      void Tracking.send(TrackingEvent.multiActionsWithTasksSelectWithTaskMenu)
    }
    if (
      multiActionsWithTasksStore.state.visible &&
      multiActionsWithTasksStore.state.tasksIds.size === 0
    ) {
      multiActionsWithTasksStore.setTasksId(task.id)
    }
  }

  return (
    <TaskMenuNewBase
      {...props}
      toggleComplete={toggleComplete}
      toggleArchive={toggleArchive}
      deleteTask={deleteTask}
      moveTask={moveTask}
      selectMultipleTasksToggleOn={selectMultipleTasksToggleOn}
      hub={hub}
    />
  )
}
